import { saveScore, getLeaderboard } from '../config/googleSheets.js';

// Local storage key for offline leaderboard
const STORAGE_KEY = 'beanwars_leaderboard';

export class LeaderboardManager {
  constructor() {
    this.localScores = this.loadLocalScores();
  }

  loadLocalScores() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error loading local scores:', e);
      return [];
    }
  }

  saveLocalScore(scoreData) {
    try {
      // Convert to snake_case to match Supabase format
      const hasEmail = !!(scoreData.playerEmail && scoreData.playerEmail.includes('@'));
      const hasWallet = !!(scoreData.playerWallet && scoreData.playerWallet.startsWith('0x'));
      const normalizedScore = {
        player_name: scoreData.playerName,
        player_email: scoreData.playerEmail,
        player_wallet: scoreData.playerWallet,
        has_email: hasEmail,
        has_wallet: hasWallet,
        prize_eligible: hasEmail || hasWallet,
        character_id: scoreData.characterId,
        character_name: scoreData.characterName,
        character_image: scoreData.characterImage,
        side: scoreData.side,
        score: scoreData.score,
        level_reached: scoreData.levelReached,
        run_time: scoreData.runTime,
        specials_used: scoreData.specialsUsed,
        hearts_remaining: scoreData.heartsRemaining,
        created_at: new Date().toISOString()
      };

      this.localScores.push(normalizedScore);
      this.localScores.sort((a, b) => b.score - a.score);
      this.localScores = this.localScores.slice(0, 100); // Keep top 100
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.localScores));
    } catch (e) {
      console.error('Error saving local score:', e);
    }
  }

  async saveScore(scoreData) {
    // Save locally first (always succeeds)
    this.saveLocalScore(scoreData);
    console.log('[Leaderboard] Score saved to localStorage');

    // Try to save to cloud (Google Apps Script via Vercel proxy)
    try {
      console.log('[Leaderboard] Submitting score to cloud...');
      const result = await saveScore(scoreData);

      if (result.success) {
        console.log('[Leaderboard] Score submitted to cloud successfully!');
      } else {
        console.error('[Leaderboard] Cloud submission failed:', result.error?.message || 'Unknown error');
      }

      return result;
    } catch (error) {
      console.error('[Leaderboard] Error saving to cloud:', error);
      return { success: false, error };
    }
  }

  async getScores(side = 'all', verified = false) {
    // Always try to fetch from Google Apps Script first for global leaderboard
    try {
      console.log('[Leaderboard] Fetching scores from cloud...', verified ? '(verified only)' : '');
      const result = await getLeaderboard(side, 500, verified);

      if (result.success && result.data && result.data.length > 0) {
        console.log('[Leaderboard] Loaded global leaderboard:', result.data.length, 'scores');
        return result.data;
      }

      // If cloud returned empty (not an error), show empty state
      if (result.success) {
        console.log('[Leaderboard] Cloud connected but no scores yet - showing empty state');
        return [];
      }

      // Only fall back to localStorage if cloud failed (network error, etc.)
      console.warn('[Leaderboard] Cloud fetch failed:', result.error?.message || 'Unknown error');
    } catch (error) {
      console.error('[Leaderboard] Error fetching from cloud:', error);
    }

    // Fallback to local scores only on error
    console.log('[Leaderboard] Using localStorage fallback');
    let scores = this.localScores;

    if (side !== 'all') {
      scores = scores.filter(score => score.side === side);
    }

    if (verified) {
      scores = scores.filter(score => score.prize_eligible || score.has_email || score.has_wallet);
    }

    return scores;
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}

export const leaderboardManager = new LeaderboardManager();
